import { useRef, useState } from 'react';

import Box from '@zenbusiness/zui/Box';
import IconButton from '@zenbusiness/zui/IconButton';
import MuiMenuItem from '@zenbusiness/zui/MenuItem';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import Tooltip from '@zenbusiness/zui/Tooltip';
import copy from 'copy-to-clipboard';
import PropTypes from 'prop-types';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import useBreakpoints from '#client/hooks/useBreakpoints';

import { TooltipStyle } from './styles';

const DEFAULT_DISPLAY_TIME = 800;

const TooltipStyled = styled(({ className, ...props }) => (
  <Tooltip
    {...props}
    aria-label="copy button"
    classes={{ popper: className }}
  />
))(TooltipStyle);

export default function MenuItemWithCopy({
  copyText,
  title,
  texts,
  callback,
  activeColor = 'blue'
}) {
  const copyButtonRef = useRef();
  const {
    sizes: { isDesktop }
  } = useBreakpoints();

  const [showTooltip, setShowTooltip] = useState(false);

  const placement = isDesktop && texts.some((text) => text.length >= 16) ? 'top' : 'right';

  const handleClick = () => {
    copy(copyText);

    setShowTooltip(true);
    copyButtonRef.current.blur();

    setTimeout(() => {
      setShowTooltip(false);
    }, DEFAULT_DISPLAY_TIME);
    if (typeof callback === 'function') {
      callback();
    }
  };

  return (
    <MuiMenuItem>
      <Stack gap="8px">
        <Text
          color="tertiary"
          size="xs"
        >
          {title}
        </Text>
        <Stack
          alignItems="center"
          direction="row"
          gap="4px"
          justifyContent="space-between"
        >
          <Box>
            {texts.map((text) => (
              <Text
                color={showTooltip ? activeColor : 'primary'}
                component="p"
                key={`${title}-${text}`}
                size="md"
                weight="medium"
              >
                {text}
              </Text>
            ))}
          </Box>
          <TooltipStyled
            open={showTooltip}
            placement={placement}
            title="Copied"
          >
            <IconButton
              aria-label="copy-button"
              label="copy-button"
              onClick={handleClick}
              ref={copyButtonRef}
            >
              <ContentCopyIcon
                color={showTooltip ? activeColor : 'primary'}
                fontSize="small"
              />
            </IconButton>
          </TooltipStyled>
        </Stack>
      </Stack>
    </MuiMenuItem>
  );
}

MenuItemWithCopy.propTypes = {
  copyText: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  texts: PropTypes.arrayOf(PropTypes.string).isRequired,
  callback: PropTypes.func,
  activeColor: PropTypes.string
};
